import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const AdminActivityPanel = withListOverflow(AdminActivityListOwner);
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AuthError } from "../../data/errors.js";
import {
  getLoginHistory,
  getUsageAnalytics,
} from "../../data/usageAnalyticsClient.js";
import { getSignedInUsers } from "../../data/adminUsersClient.js";
import { T } from "../../app/tokens.js";
import { AdminUserActivityDrawer } from "./AdminUserActivityDrawer.jsx";

const PERIOD_OPTIONS = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

const PANEL = {
  border: `1px solid ${T.border}`,
  borderRadius: 8,
  background: T.white,
};

function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, "0")}s`;
}

function Kpi({ label, value, detail, testId }) {
  return (
    <div data-testid={testId} style={{ ...PANEL, padding: "16px 18px" }}>
      <div style={{ color: T.mid, fontSize: T.fsXs, fontWeight: T.fwSemibold, textTransform: "uppercase", letterSpacing: ".06em" }}>
        {label}
      </div>
      <div style={{ color: T.dark, fontSize: T.fsXl, fontWeight: T.fwBold, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </div>
      <div style={{ color: T.light, fontSize: T.fsXs, marginTop: 4 }}>{detail}</div>
    </div>
  );
}

function MiniBarChart({ trend }) {
  const max = Math.max(1, ...trend.map((point) => Math.max(point.activeUsers, point.logins)));
  return (
    <div data-testid="admin-activity-trend-chart" style={{ ...PANEL, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
        <div>
          <h3 style={{ margin: 0, color: T.dark, fontSize: T.fsBase5 }}>Active users and logins</h3>
          <p style={{ margin: "5px 0 0", color: T.mid, fontSize: T.fsXs }}>Daily activity across captured sessions</p>
        </div>
        <div style={{ display: "flex", gap: 14, color: T.mid, fontSize: T.fsXs }}>
          <span><i style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: T.blue, marginRight: 5 }} />Users</span>
          <span><i style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: T.teal, marginRight: 5 }} />Logins</span>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "end", gap: 6, height: 150, marginTop: 22, padding: "0 4px 20px", borderBottom: `1px solid ${T.border}` }}>
        {trend.map((point) => (
          <div key={point.date} title={`${point.date}: ${point.activeUsers} users, ${point.logins} logins`} style={{ flex: 1, minWidth: 8, height: "100%", display: "flex", alignItems: "end", gap: 2 }}>
            <div style={{ width: "50%", height: `${Math.max(3, (point.activeUsers / max) * 100)}%`, background: T.blue, borderRadius: "3px 3px 0 0" }} />
            <div style={{ width: "50%", height: `${Math.max(3, (point.logins / max) * 100)}%`, background: T.teal, borderRadius: "3px 3px 0 0" }} />
          </div>
        ))}
      </div>
      {trend.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", color: T.light, fontSize: T.fsXs, marginTop: 8 }}>
          <span>{trend[0].date}</span>
          <span>{trend[trend.length - 1].date}</span>
        </div>
      )}
    </div>
  );
}

function AdminActivityListOwner({ explainer } = {}) {
  const [periodDays, setPeriodDays] = useState(30);
  const [analytics, setAnalytics] = useState(null);
  const [state, setState] = useState({ loading: true, error: null });
  const [users, setUsers] = useState([]);
  const [loginFilters, setLoginFilters] = useState({
    userId: "",
    search: "",
    from: "",
    to: "",
    sort: "desc",
  });
  const [loginPage, setLoginPage] = useState(1);
  const [loginState, setLoginState] = useState({ loading: true, error: null, data: null });
  const loginRequestId = useRef(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserState, setSelectedUserState] = useState({ loading: false, error: null });

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const nextAnalytics = await getUsageAnalytics(periodDays);
      setAnalytics(nextAnalytics);
      setState({ loading: false, error: null });
    } catch (error) {
      setState({ loading: false, error });
    }
  }, [periodDays]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    const userId = new URLSearchParams(window.location.search).get("userId");
    getSignedInUsers()
      .then((data) => {
        if (cancelled) return;
        const roster = data.users || [];
        setUsers(roster);
        if (userId) {
          const user = roster.find((candidate) => candidate.id === userId);
          if (user) setSelectedUser(user);
          setSelectedUserState({ loading: false, error: user ? null : new Error("User not found") });
        }
      })
      .catch((error) => {
        if (!cancelled && userId) setSelectedUserState({ loading: false, error });
      });
    if (userId) setSelectedUserState({ loading: true, error: null });
    return () => { cancelled = true; };
  }, []);

  const loadLogins = useCallback(async () => {
    const requestId = ++loginRequestId.current;
    setLoginState((current) => ({ ...current, loading: true, error: null }));
    try {
      const data = await getLoginHistory({ ...loginFilters, page: loginPage, pageSize: 25 });
      if (loginRequestId.current === requestId) setLoginState({ loading: false, error: null, data });
    } catch (error) {
      if (loginRequestId.current === requestId) setLoginState({ loading: false, error, data: null });
    }
  }, [loginFilters, loginPage]);
  useEffect(() => { void loadLogins(); }, [loadLogins]);

  const setLoginFilter = (key, value) => {
    setLoginFilters((current) => ({ ...current, [key]: value }));
    setLoginPage(1);
  };
  const isEmpty = !state.loading
    && !state.error
    && (!analytics || (analytics.trend || []).length === 0);
  const topSurfaces = analytics?.topSurfaces ?? [];
  const maxSurfaceViews = Math.max(1, ...topSurfaces.map((surface) => surface.pageViews));

  return (
    <section data-testid="admin-activity-panel">
      <h2 style={{ margin: 0, color: T.dark, fontSize: T.fsLg5 }}>Activity</h2>
      {explainer}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
        <label style={{ color: T.mid, fontSize: T.fsXs, whiteSpace: "nowrap" }}>
          Period{" "}
          <select
            data-testid="admin-activity-period"
            value={periodDays}
            onChange={(event) => setPeriodDays(Number(event.target.value))}
            style={{ marginLeft: 6, padding: "7px 9px", border: `1px solid ${T.border}`, borderRadius: 5, background: T.white }}
          >
            {PERIOD_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
      </div>

      {state.loading && <div data-testid="admin-activity-loading" style={{ color: T.mid, padding: "28px 0" }}>Loading usage analytics…</div>}
      {state.error && (
        <div data-testid="admin-activity-error" role="alert" style={{ ...PANEL, borderColor: T.red, color: T.red, padding: 16 }}>
          {state.error instanceof AuthError ? "You are not allowed to view usage analytics." : "Usage analytics are temporarily unavailable."}
          <button type="button" onClick={() => void load()} style={{ marginLeft: 12 }}>Retry</button>
        </div>
      )}
      {isEmpty && <div data-testid="admin-activity-empty" style={{ ...PANEL, color: T.mid, padding: 28 }}>No usage activity has been captured for this period.</div>}
      {selectedUserState.loading && <div data-testid="admin-activity-user-loading" style={{ color: T.mid, marginBottom: 16 }}>Loading selected user activity…</div>}
      {selectedUserState.error && <div data-testid="admin-activity-user-error" role="alert" style={{ ...PANEL, color: T.mid, padding: 16, marginBottom: 16 }}>The selected user could not be found in the signed-in roster.</div>}

      {!state.loading && !state.error && !isEmpty && analytics && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, marginBottom: 16 }}>
            <Kpi label="Daily active users" value={analytics.kpis.dau} detail={`Weekly ${analytics.kpis.wau} · Monthly ${analytics.kpis.mau}`} testId="admin-activity-kpi-dau" />
            <Kpi label="Logins" value={analytics.kpis.logins} detail={`Across ${periodDays} days`} testId="admin-activity-kpi-logins" />
            <Kpi label="Average session" value={analytics.kpis.averageSessionLabel || formatDuration(analytics.kpis.averageSessionSeconds)} detail="Active time; gaps over 30 minutes split sessions" testId="admin-activity-kpi-session" />
            <Kpi label="Surfaces per session" value={Number(analytics.kpis.surfacesPerSession || 0).toFixed(1)} detail="Distinct surfaces" testId="admin-activity-kpi-surfaces" />
          </div>
          <MiniBarChart trend={analytics.trend || []} />
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, .8fr)", gap: 16, marginTop: 16 }}>
            <div data-testid="admin-activity-top-surfaces" style={{ ...PANEL, padding: 20 }}>
              <h3 style={{ margin: 0, color: T.dark, fontSize: T.fsBase5 }}>Top surfaces</h3>
              <p style={{ margin: "5px 0 14px", color: T.mid, fontSize: T.fsXs }}>Route-level page views, grouped by surface.</p>
              {topSurfaces.length === 0 && <p style={{ color: T.mid, fontSize: T.fsSm }}>No page views captured.</p>}
              {topSurfaces.map((surface) => (
                <div key={surface.surface} style={{ marginTop: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", color: T.dark, fontSize: T.fsSm }}>
                    <span>{surface.surface}</span><strong>{surface.pageViews}</strong>
                  </div>
                  <div style={{ height: 7, background: T.lightGray, borderRadius: 8, marginTop: 5 }}>
                    <div style={{ height: "100%", width: `${(surface.pageViews / maxSurfaceViews) * 100}%`, background: T.blue, borderRadius: 8 }} />
                  </div>
                </div>
              ))}
            </div>
            <div data-testid="admin-activity-role-adoption" style={{ ...PANEL, padding: 20 }}>
              <h3 style={{ margin: 0, color: T.dark, fontSize: T.fsBase5 }}>Role adoption</h3>
              <p style={{ margin: "5px 0 14px", color: T.mid, fontSize: T.fsXs }}>Distinct active users by role.</p>
              {(analytics.roleAdoption || []).map((role) => (
                <div key={role.role} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${T.border}`, color: T.dark, fontSize: T.fsSm }}>
                  <span style={{ textTransform: "capitalize" }}>{role.role.replace("_", " ")}</span><strong>{role.users}</strong>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      <section data-testid="admin-login-history" style={{ ...PANEL, marginTop: 18, overflow: "hidden" }}>
        <div style={{ padding: 20, borderBottom: `1px solid ${T.border}` }}>
          <h3 style={{ margin: 0, color: T.dark, fontSize: T.fsBase5 }}>Login history</h3>
          <p style={{ margin: "5px 0 14px", color: T.mid, fontSize: T.fsXs }}>
            Retained successful sign-ins only. Older sign-ins are not reconstructed.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "end" }}>
            <label style={{ display: "grid", gap: 4, color: T.mid, fontSize: T.fsXs }}>
              Search user
              <input data-testid="admin-login-search" value={loginFilters.search} onChange={(event) => setLoginFilter("search", event.target.value)} placeholder="Name or email" />
            </label>
            <label style={{ display: "grid", gap: 4, color: T.mid, fontSize: T.fsXs }}>
              User
              <select data-testid="admin-login-user-filter" value={loginFilters.userId} onChange={(event) => setLoginFilter("userId", event.target.value)}>
                <option value="">All users</option>
                {users.map((user) => <option key={user.id} value={user.id}>{user.displayName} · {user.email}</option>)}
              </select>
            </label>
            <label style={{ display: "grid", gap: 4, color: T.mid, fontSize: T.fsXs }}>
              From
              <input data-testid="admin-login-from" type="date" value={loginFilters.from} onChange={(event) => setLoginFilter("from", event.target.value)} />
            </label>
            <label style={{ display: "grid", gap: 4, color: T.mid, fontSize: T.fsXs }}>
              To
              <input data-testid="admin-login-to" type="date" value={loginFilters.to} onChange={(event) => setLoginFilter("to", event.target.value)} />
            </label>
            <label style={{ display: "grid", gap: 4, color: T.mid, fontSize: T.fsXs }}>
              Date order
              <select data-testid="admin-login-sort" value={loginFilters.sort} onChange={(event) => setLoginFilter("sort", event.target.value)}>
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </select>
            </label>
          </div>
        </div>
        {loginState.loading && <div data-testid="admin-login-loading" style={{ padding: 20, color: T.mid }}>Loading login history…</div>}
        {loginState.error && (
          <div data-testid="admin-login-error" role="alert" style={{ padding: 20, color: T.red }}>
            Login history could not be loaded.
            <button type="button" onClick={() => void loadLogins()} style={{ marginLeft: 10 }}>Retry</button>
          </div>
        )}
        {!loginState.loading && !loginState.error && (loginState.data?.logins || []).length === 0 && (
          <div data-testid="admin-login-empty" style={{ padding: 20, color: T.mid }}>No captured logins match these filters.</div>
        )}
        {!loginState.loading && !loginState.error && (loginState.data?.logins || []).length > 0 && (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                <thead>
                  <tr style={{ color: T.mid, fontSize: T.fsXs, textAlign: "left", background: T.lightGray }}>
                    {["Timestamp", "User", "Role", "Authentication", "Environment", "IP address"].map((heading) => <th key={heading} style={{ padding: "10px 14px", fontWeight: T.fwSemibold }}>{heading}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {loginState.data.logins.map((login) => (
                    <tr key={login.id} data-testid="admin-login-row" style={{ borderTop: `1px solid ${T.border}`, color: T.dark, fontSize: T.fsSm }}>
                      <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>{new Date(login.occurredAt).toLocaleString()}</td>
                      <td style={{ padding: "12px 14px" }}>
                        {login.userId ? (
                          <button type="button" onClick={() => setSelectedUser({ id: login.userId, displayName: login.displayName, email: login.email, role: login.role, status: "unknown", oktaGroups: [] })} style={{ padding: 0, border: 0, color: T.blue, background: "transparent", textAlign: "left", cursor: "pointer" }}>
                            <strong>{login.displayName}</strong><br />{login.email || "No email"}
                          </button>
                        ) : <><strong>{login.displayName}</strong><br />{login.email || "No email"}</>}
                      </td>
                      <td style={{ padding: "12px 14px", textTransform: "capitalize" }}>{login.role?.replace("_", " ") || "—"}</td>
                      <td style={{ padding: "12px 14px" }}>{login.authMethod || "—"}</td>
                      <td style={{ padding: "12px 14px", textTransform: "capitalize" }}>{login.environment || "—"}</td>
                      <td style={{ padding: "12px 14px" }}>{login.clientIp || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${T.border}`, color: T.mid, fontSize: T.fsXs }}>
              <span>Page {loginState.data.page} · {loginState.data.total} sign-ins</span>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" data-testid="admin-login-previous" disabled={loginPage <= 1} onClick={() => setLoginPage((page) => Math.max(1, page - 1))}>Previous</button>
                <button type="button" data-testid="admin-login-next" disabled={!loginState.data.hasNext} onClick={() => setLoginPage((page) => page + 1)}>Next</button>
              </div>
            </div>
          </>
        )}
      </section>
      {selectedUser && <AdminUserActivityDrawer user={selectedUser} onClose={() => setSelectedUser(null)} />}
    </section>
  );
}